import { useMemo } from "react";
import {
  Box,
  ColumnLayout,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import { CanonicalBusinessEvent } from "../../types/cbes";
import {
  calculateBundleRevenueSummary,
  generateBundleRevenueTimeline,
  hasMultipleProducts,
  hasBundleOrder,
  BundleRevenueTimelineEntry,
} from "../../helpers/bundleRevenueUtils";
import { formatCurrency, formatDateTime } from "../../helpers/dbdUtils";

interface BundleRevenueTimelineProps {
  cbes: CanonicalBusinessEvent[];
  totalOrderValue?: number;
  productCount?: number;
  dbdProducts?: { [key: string]: any };
}

const COLUMN_DEFINITIONS: TableProps<BundleRevenueTimelineEntry>["columnDefinitions"] =
  [
    {
      id: "date",
      header: "Date",
      cell: (item) => (item.date ? formatDateTime(item.date) : "-"),
      sortingField: "date",
    },
    {
      id: "product",
      header: "Product",
      cell: (item) => (
        <Box variant={item.eventType === "prepaid" ? "strong" : "span"}>
          {item.productId}
        </Box>
      ),
    },
    {
      id: "deferred",
      header: "Deferred Revenue Balance",
      cell: (item) => (
        <Box
          color={item.deferredBalance > 0 ? "text-status-warning" : undefined}
        >
          {formatCurrency(item.deferredBalance, item.currencyCode)}
        </Box>
      ),
    },
    {
      id: "recognized",
      header: "Recognised Revenue Balance",
      cell: (item) => (
        <Box
          color={item.recognizedTotal > 0 ? "text-status-success" : undefined}
        >
          {formatCurrency(item.recognizedTotal, item.currencyCode)}
        </Box>
      ),
    },
    {
      id: "amount",
      header: "Product Amount",
      cell: (item) => (
        <Box variant={item.eventType === "prepaid" ? "strong" : "span"}>
          {item.eventType === "pending"
            ? "-"
            : formatCurrency(item.amount, item.currencyCode)}
        </Box>
      ),
    },
    {
      id: "event",
      header: "Event",
      cell: (item) => {
        if (item.eventType === "prepaid") {
          return <StatusIndicator type="info">Deferred Charge</StatusIndicator>;
        }
        if (item.eventType === "fulfilled") {
          return <StatusIndicator type="success">Fulfilled</StatusIndicator>;
        }
        return <StatusIndicator type="pending">Pending</StatusIndicator>;
      },
    },
  ];

export function BundleRevenueTimeline({
  cbes,
  totalOrderValue,
  productCount,
  dbdProducts,
}: BundleRevenueTimelineProps) {
  const summary = useMemo(
    () => calculateBundleRevenueSummary(cbes, totalOrderValue, productCount),
    [cbes, totalOrderValue, productCount]
  );

  const timeline = useMemo(
    () =>
      generateBundleRevenueTimeline(
        cbes,
        totalOrderValue,
        productCount,
        dbdProducts
      ),
    [cbes, totalOrderValue, productCount, dbdProducts]
  );

  if (!cbes?.length || !hasBundleOrder(cbes) || !hasMultipleProducts(cbes))
    return null;
  if (!summary || !timeline.length) return null;

  return (
    <SpaceBetween size="l">
      <ColumnLayout columns={4} variant="text-grid">
        <div>
          <Box variant="awsui-key-label">Total Order Value</Box>
          <Box variant="h3">
            {formatCurrency(summary.totalOrderValue, summary.currencyCode)}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Deferred Revenue</Box>
          <Box variant="h3" color="text-status-warning">
            {formatCurrency(summary.deferredRevenue, summary.currencyCode)}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Recognised Revenue</Box>
          <Box variant="h3" color="text-status-success">
            {formatCurrency(summary.recognizedRevenue, summary.currencyCode)}
          </Box>
        </div>
        <div>
          <Box variant="awsui-key-label">Completion</Box>
          <SpaceBetween size="xs">
            <ProgressBar
              value={summary.completionPercentage}
              status={
                summary.completionPercentage === 100 ? "success" : "in-progress"
              }
            />
            <Box variant="small">
              {summary.fulfilledCount} of {summary.totalCount} products (
              {summary.completionPercentage.toFixed(0)}%)
            </Box>
          </SpaceBetween>
        </div>
      </ColumnLayout>

      <Table
        columnDefinitions={COLUMN_DEFINITIONS}
        items={timeline}
        variant="embedded"
        empty={
          <Box textAlign="center" color="inherit">
            <b>No timeline data</b>
          </Box>
        }
      />
    </SpaceBetween>
  );
}
